import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/internal/operators';
import { ActivatedRoute, Router } from '@angular/router';
// import * as Sentry from '@sentry/angular';
import { UserService } from '../../../services/user/user.service';
import { ServerSideRenderingService } from '@coreservices/server-side-rendering.service';
import { AuthService } from '@coreservices/auth.service';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

  isRefreshingToken: boolean = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private _router: Router,
    private _ssr: ServerSideRenderingService,
    private _userService: UserService,
    private _activateRoute: ActivatedRoute,
    private _authService: AuthService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // this._alertService.showError('activation', 'activation');
    this._activateRoute.queryParamMap.subscribe(params => {
      const ref = params.get('ref');

      if (ref && localStorage.getItem('ref') === null) {
        localStorage.setItem('ref', ref);
      }
    });

    if(this._ssr.isBrowser) {
      return next.handle(req).catch(error => {
        if (error instanceof HttpErrorResponse) {
          if(req.url.includes('refresh')) {
            this._userService.deleteClient();
            this._router.navigate(['/login']);
          } else {
            switch ((<HttpErrorResponse>error).status) {
                case 400|403|404|412|422|500|503:
                  return this.handleError(req, error);
                case 401:
                  return this.handleUnauthorized(req, next);
            }
          }
        } else {
            return throwError(error);
        }
      });
    }
  }

  handleError(req, error): Observable<any> {
    if (typeof error?.error?.error === 'string' && error?.error?.error?.includes('No Portal found')) {
      this._router.navigate(['/not-found']);
    }

    if (req.url.includes('m3u8') || req.url.includes('prolongate') && (error?.status === 401 || error?.status === 403)) {
      return;
    }

    if(typeof error?.error?.error === 'string' && error?.error?.error?.includes('no_quiz_versions')) {
      return;
    }

    let errorMessage = '';
    if (error.error instanceof HttpErrorResponse) {
      // client-side error
      errorMessage = `>>> CLIENT SIDE ERROR\nMessage: ${error.message}`;
    } else {
      // server-side error
      switch (error.status) {
        case 400:
          errorMessage = 'Bad Request.';
          break;
        case 401:
          errorMessage = 'You need to log in to do this action.';
          break;
        case 403:
          errorMessage = 'You do not have permission to access the requested resource.';
          break;
        case 404:
          errorMessage = 'The requested resource does not exist.';
          break;
        case 412:
          errorMessage = 'Precondition Failed.';
          break;
        case 422:
          errorMessage = 'Validation Error!';
          break;
        case 500:
          errorMessage = 'Internal Server Error.';
          break;
        case 503:
          errorMessage = 'The requested service is not available.';
          break;
        default:
          errorMessage = 'Something went wrong!';
      }
      errorMessage = `>>> SERVER SIDE ERROR\nCode: ${error.status}\nURL: ${error.url}\nMessage: ${errorMessage}`;
    }

    console.error(errorMessage);

    // this.sentryTransaction(req, error);
    return throwError(errorMessage);
  }

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const newBody = { ...JSON.parse(req.body), token: token };
    return req.clone({ body: newBody })
  }

  handleUnauthorized (req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshingToken) {
        this.isRefreshingToken = true;

        // Reset here so that the following requests wait until the token
        // comes back from the refreshToken call.
        this.tokenSubject.next(null);
        // get a new token via userService.refreshToken
        return this._authService?.tokenRefresh()
            .pipe(switchMap((newTokens: { access_token: string, refresh_token: string }) => {
                this._userService.setTokenPair(newTokens)
                // did we get a new token retry previous request
                if (newTokens) {
                    this.tokenSubject.next(newTokens.access_token);
                    return next.handle(this.addToken(req, newTokens.access_token));
                }

                // If we don't get a new token, we are in trouble so logout.
                this._userService.deleteClient();
                this._router.navigate(['/login']);
                return throwError('');
            }), catchError(error => {
                    // If there is an exception calling 'refreshToken', bad news so logout.
                    this._userService.deleteClient();
                    this._router.navigate(['/login']);
                    return throwError('');
                }), finalize(() => {
                    this.isRefreshingToken = false;
                })
            );
    } else {
        return this.tokenSubject
            .pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    return next.handle(this.addToken(req, token));
                })
            );
    }
}

  // sentryTransaction(req, error): void {
  //   const scope = new Sentry.Scope();
  //   scope.setTransactionName('Request error');
  //   if (this._userService.client) {
  //     scope.setUser(this._userService.client);
  //   }
  //   scope.setTag('locale', this._translateService.currentLang);
  //   scope.setContext('HttpRequest', req);
  //   scope.setContext('HttpErrorResponse', error);
  //   Sentry.captureException(new Error(JSON.stringify(req.urlWithParams)), () => scope);
  // }
}
